'use client';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, ListGroup, Modal, Spinner, Alert } from 'react-bootstrap';
import { Plus, Search, Edit2, Trash2, FileText, ChevronDown, ChevronUp, Database } from 'lucide-react';
import { ConfigReader } from '../../config/ConfigReader';
import {
  CsAgentClient,
  type KnowledgeBaseDocument,
  type GetDocumentResponse,
  type SearchResult,
} from '../../infrastructure/CsAgentClient';
import {
  CHATBOT_TYPE_LABELS,
  DEFAULT_CHATBOT_TYPE,
  VISIBLE_CHATBOT_TYPES,
  type ChatbotType,
} from '../../model/ChatbotType';

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = ['manual', 'faq', 'policy'] as const;
const LANGUAGE_OPTIONS = ['en', 'ko', 'ja', 'zh'] as const;
const CHUNK_TRUNCATE_LENGTH = 200;

const SOURCE_BADGE_VARIANT: Record<string, string> = {
  manual: 'primary',
  faq: 'success',
  policy: 'warning',
};

function getSourceBadge(source: string) {
  return (
    <Badge bg={SOURCE_BADGE_VARIANT[source] ?? 'secondary'} className="ms-1">
      {source}
    </Badge>
  );
}

function formatDate(dateStr?: string): string {
  if (!dateStr) {
    return '-';
  }
  try {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

// ─── Chunk Card ───────────────────────────────────────────────────────────────

function ChunkCard({ chunk, index }: { chunk: Record<string, unknown>; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const text = (chunk['text'] as string) ?? (chunk['content'] as string) ?? JSON.stringify(chunk);
  const charCount = text.length;
  const needsTruncation = charCount > CHUNK_TRUNCATE_LENGTH;

  return (
    <Card className="mb-2">
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="fw-bold text-muted">Chunk #{index}</small>
          <small className="text-muted">{charCount} chars</small>
        </div>
        <p className="mb-1" style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
          {expanded || !needsTruncation ? text : `${text.slice(0, CHUNK_TRUNCATE_LENGTH)}...`}
        </p>
        {needsTruncation && (
          <Button variant="link" size="sm" className="p-0" onClick={() => setExpanded((v) => !v)}>
            {expanded ? (
              <>
                <ChevronUp size={14} /> Collapse
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Expand
              </>
            )}
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────────────

export default function KnowledgeBasePage() {
  const client = useMemo(() => {
    const apiUrl = ConfigReader.getInstance().getConfig().apiUrl;
    return new CsAgentClient(apiUrl);
  }, []);

  // Document list state
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Filter state
  const [serviceType, setServiceType] = useState<ChatbotType>(DEFAULT_CHATBOT_TYPE);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selection & detail state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [documentDetail, setDocumentDetail] = useState<GetDocumentResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formSource, setFormSource] = useState<string>('manual');
  const [formLanguage, setFormLanguage] = useState<string>('ko');
  const [formContent, setFormContent] = useState('');

  // Search test state
  const [searchTestQuery, setSearchTestQuery] = useState('');
  const [searchTestResults, setSearchTestResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTestError, setSearchTestError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedDocumentId(null);
    setDocumentDetail(null);
    setDetailError(null);
    setSearchTestQuery('');
    setSearchTestResults([]);
    setSearchTestError(null);
  }, [serviceType]);

  // ─── Data fetching ────────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);
    try {
      const res = await client.listDocuments(sourceFilter || undefined, serviceType);
      setDocuments(res.documents ?? []);
      setTotalCount(res.total ?? 0);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoadingList(false);
    }
  }, [client, sourceFilter, serviceType]);

  const fetchDocumentDetail = useCallback(
    async (docId: string) => {
      setIsLoadingDetail(true);
      setDetailError(null);
      try {
        const res = await client.getDocument(docId, serviceType);
        setDocumentDetail(res);
      } catch (err) {
        setDetailError(err instanceof Error ? err.message : 'Failed to load document detail');
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [client, serviceType],
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (selectedDocumentId) {
      fetchDocumentDetail(selectedDocumentId);
    } else {
      setDocumentDetail(null);
    }
  }, [selectedDocumentId, fetchDocumentDetail]);

  // ─── Derived state ────────────────────────────────────────────────────

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) {
      return documents;
    }
    const q = searchQuery.toLowerCase();
    return documents.filter((d) => d.title.toLowerCase().includes(q));
  }, [documents, searchQuery]);

  const detailDoc = documentDetail?.document;
  const detailChunks = documentDetail?.chunks ?? [];

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleSelectDocument = (docId: string) => {
    setSelectedDocumentId(docId);
    setSearchTestResults([]);
    setSearchTestError(null);
  };

  const handleOpenCreateModal = () => {
    setFormTitle('');
    setFormSource('manual');
    setFormLanguage('ko');
    setFormContent('');
    setModalError(null);
    setShowCreateModal(true);
  };

  const handleOpenEditModal = () => {
    if (!detailDoc) {
      return;
    }
    setFormTitle(detailDoc.title);
    setFormSource(detailDoc.source);
    setFormLanguage(detailDoc.language ?? 'ko');
    // Reconstruct content from chunks if available, otherwise use document content
    if (detailDoc.content) {
      setFormContent(detailDoc.content);
    } else if (detailChunks.length > 0) {
      setFormContent(
        detailChunks
          .map((c) => (c['text'] as string) ?? (c['content'] as string) ?? '')
          .filter(Boolean)
          .join('\n\n'),
      );
    } else {
      setFormContent('');
    }
    setModalError(null);
    setShowEditModal(true);
  };

  const handleCreate = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      setModalError('Title and content are required.');
      return;
    }
    setIsSaving(true);
    setModalError(null);
    try {
      const res = await client.createDocument(
        {
          title: formTitle.trim(),
          source: formSource,
          language: formLanguage,
          content: formContent.trim(),
        },
        serviceType,
      );
      setShowCreateModal(false);
      await fetchDocuments();
      // Select the newly created document
      const newId = res?.documentId ?? res?.document?.documentId;
      if (newId) {
        setSelectedDocumentId(newId);
      }
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to create document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedDocumentId || !formTitle.trim()) {
      setModalError('Title is required.');
      return;
    }
    setIsSaving(true);
    setModalError(null);
    try {
      await client.updateDocument(
        selectedDocumentId,
        {
          title: formTitle.trim(),
          source: formSource,
          language: formLanguage,
          content: formContent.trim() || '',
        },
        serviceType,
      );
      setShowEditModal(false);
      await fetchDocuments();
      await fetchDocumentDetail(selectedDocumentId);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to update document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocumentId) {
      return;
    }
    setIsDeleting(true);
    setModalError(null);
    try {
      await client.deleteDocument(selectedDocumentId, serviceType);
      setShowDeleteModal(false);
      setSelectedDocumentId(null);
      setDocumentDetail(null);
      await fetchDocuments();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchTest = async () => {
    if (!searchTestQuery.trim()) {
      return;
    }
    setIsSearching(true);
    setSearchTestError(null);
    try {
      const res = await client.searchKnowledgeBase(searchTestQuery.trim(), 5, serviceType);
      setSearchTestResults(res.results ?? []);
    } catch (err) {
      setSearchTestError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <Database size={28} className="me-2" />
            Knowledge Base Management
          </h2>
          <p className="text-muted mb-0">
            {totalCount} documents
            {detailChunks.length > 0 && selectedDocumentId ? `, ${detailChunks.length} chunks (selected)` : ''}
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <Plus size={16} className="me-1" />
          New Document
        </Button>
      </div>

      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search documents by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All Sources</option>
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            aria-label="Service type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value as ChatbotType)}
          >
            {VISIBLE_CHATBOT_TYPES.map((type) => (
              <option key={type} value={type}>
                {CHATBOT_TYPE_LABELS[type]}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {listError && (
        <Alert variant="danger" dismissible onClose={() => setListError(null)}>
          {listError}
        </Alert>
      )}

      <Row>
        {/* Left Panel - Document List */}
        <Col md={6}>
          <Card style={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
            <Card.Header className="bg-light fw-bold py-2">
              <FileText size={16} className="me-1" />
              Documents ({filteredDocuments.length})
            </Card.Header>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {isLoadingList ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" /> Loading...
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center text-muted py-5">No documents found.</div>
              ) : (
                <ListGroup variant="flush">
                  {filteredDocuments.map((doc) => (
                    <ListGroup.Item
                      key={doc.documentId}
                      action
                      active={selectedDocumentId === doc.documentId}
                      onClick={() => handleSelectDocument(doc.documentId)}
                      className="py-2"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="fw-bold text-truncate">{doc.title}</div>
                          <small className="text-muted">Updated: {formatDate(doc.updatedAt ?? doc.createdAt)}</small>
                        </div>
                        <div className="ms-2 text-end flex-shrink-0">
                          {getSourceBadge(doc.source)}
                          {doc.language && (
                            <Badge bg="secondary" className="ms-1">
                              {doc.language}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Panel - Document Detail */}
        <Col md={6}>
          <Card style={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
            {!selectedDocumentId ? (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                <div className="text-center">
                  <FileText size={48} className="mb-3 opacity-25" />
                  <p>Select a document to view details</p>
                </div>
              </div>
            ) : isLoadingDetail ? (
              <div className="d-flex align-items-center justify-content-center h-100">
                <Spinner animation="border" size="sm" /> <span className="ms-2">Loading...</span>
              </div>
            ) : detailError ? (
              <div className="p-3">
                <Alert variant="danger">{detailError}</Alert>
              </div>
            ) : detailDoc ? (
              <>
                {/* Detail Header */}
                <Card.Header className="bg-light py-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <h5 className="mb-1 text-truncate">{detailDoc.title}</h5>
                      <div>
                        {getSourceBadge(detailDoc.source)}
                        {detailDoc.language && (
                          <Badge bg="secondary" className="ms-1">
                            {detailDoc.language}
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        ID: {detailDoc.documentId}
                      </small>
                    </div>
                    <div className="ms-2 flex-shrink-0">
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={handleOpenEditModal}>
                        <Edit2 size={14} className="me-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setModalError(null);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 size={14} className="me-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card.Header>

                {/* Scrollable body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
                  {/* Chunks Section */}
                  <h6 className="fw-bold mb-2">Chunks ({detailChunks.length})</h6>
                  {detailChunks.length === 0 ? (
                    <p className="text-muted small">No chunks available.</p>
                  ) : (
                    detailChunks.map((chunk, idx) => <ChunkCard key={idx} chunk={chunk} index={idx} />)
                  )}

                  {/* Search Test Panel */}
                  <div className="mt-4 p-3" style={{ border: '2px dashed #dee2e6', borderRadius: '0.375rem' }}>
                    <h6 className="fw-bold mb-2">
                      <Search size={16} className="me-1" />
                      Search Test
                    </h6>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Enter search query..."
                        value={searchTestQuery}
                        onChange={(e) => setSearchTestQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSearchTest();
                          }
                        }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSearchTest}
                        disabled={isSearching || !searchTestQuery.trim()}
                        className="flex-shrink-0"
                      >
                        {isSearching ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <Search size={14} className="me-1" />
                            Search
                          </>
                        )}
                      </Button>
                    </div>
                    {searchTestError && (
                      <Alert variant="danger" className="py-1 px-2 small mb-2">
                        {searchTestError}
                      </Alert>
                    )}
                    {searchTestResults.length > 0 && (
                      <div>
                        {searchTestResults.map((result, idx) => {
                          const chunkText =
                            (result.chunk['text'] as string) ??
                            (result.chunk['content'] as string) ??
                            JSON.stringify(result.chunk);
                          return (
                            <Card key={idx} className="mb-2">
                              <Card.Body className="py-2 px-3">
                                <p className="mb-1 small" style={{ whiteSpace: 'pre-wrap' }}>
                                  {chunkText.length > 300 ? `${chunkText.slice(0, 300)}...` : chunkText}
                                </p>
                                <div className="d-flex align-items-center">
                                  <small className="text-muted me-2">{result.document.title}</small>
                                  {getSourceBadge(result.document.source)}
                                </div>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                    {searchTestResults.length === 0 && !isSearching && !searchTestError && searchTestQuery.trim() && (
                      <p className="text-muted small mb-0">No results. Press Search to query.</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                Document not found.
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create / Edit Modal */}
      <Modal
        show={showCreateModal || showEditModal}
        onHide={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{showEditModal ? 'Edit Document' : 'New Document'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Document title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </Form.Group>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Source</Form.Label>
                  <Form.Select value={formSource} onChange={(e) => setFormSource(e.target.value)}>
                    {SOURCE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Language</Form.Label>
                  <Form.Select value={formLanguage} onChange={(e) => setFormLanguage(e.target.value)}>
                    {LANGUAGE_OPTIONS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>
                Content <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                placeholder="Document content..."
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                style={{ minHeight: '200px' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={showEditModal ? handleUpdate : handleCreate} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" /> Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <p>
            Are you sure you want to delete <strong>{detailDoc?.title ?? 'this document'}</strong>?
          </p>
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" /> Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
